/**
 * lib/disclosure-caret — СТВОЛ: переиспользуемая каретная-механика выпадающего
 * раскрытия (триггер с каретой `▾`↔`▴` + панель той же ширины родителя).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «ствол → ветки» + тикет
 * fe-profile §4 Q4): каретное раскрытие «как у выбора языка» нужно на нескольких
 * поверхностях — переключатель языка рейла (`lib/nav-lang`) И полоса ачивок Профиля
 * (`screens/profile-screen`). Прежде эта механика (открытие/закрытие, флип кареты,
 * aria-expanded, Esc, outside-close, reduce-motion) жила РАСПЫЛЕННОЙ в nav-lang.
 * Теперь она — ОДИН ствол; nav-lang и Профиль монтируют её тонкими ветками, НЕ
 * копируют вёрстку. Дублирование вместо ствола = REJECT ревьюера.
 *
 * SCOPE: построить контрол-обёртку (триггер-кнопка с каретой + панель-контейнер),
 *   синхронизировать `aria-expanded`/класс `--open`, обработать открытие/закрытие
 *   (клик по триггеру, Esc, клик-вне), флип кареты `▾`↔`▴` (через класс `--open`,
 *   CSS rotate), focus-возврат на триггер при закрытии по Esc. Содержимое триггера
 *   (метка слева от кареты) и содержимое панели задаёт ПОТРЕБИТЕЛЬ (Профиль кладёт
 *   полосу-превью в триггер-метку и полный грид в панель; язык — флаг+имя + список).
 *   Потребитель может навесить surface CSS-/тест-хуки на корень/триггер/панель ствола
 *   (`className` / `triggerClassName` / `panelClassName`) — это ОДНИ элементы с двумя
 *   хуками (механика ствола + вид ветки), НЕ копии вёрстки.
 *   NOT IN SCOPE: данные/список (даёт потребитель), размеры/раскладка (co-located
 *   disclosure-caret.css — панель width:100% родителя, parent-относительная),
 *   анимация раскрытия (CSS; reduce-motion → мгновенно).
 *
 * a11y (паритет nav-lang): `aria-expanded` на триггере, `aria-haspopup`, Esc
 *   закрывает + возвращает фокус на триггер, клик-вне закрывает (document
 *   pointerdown в capture). prefers-reduced-motion → CSS убирает transition.
 *
 * Cross-references: тикет fe-profile.md §4 (Q4 общий модуль) / AC-22 / AC-23;
 *   lib/nav-lang (ветка-потребитель — флаг+имя+список); styles/disclosure-caret.css
 *   (co-located, layout-plan + algebra); responsive-ui §0.E (a11y popover) / §0.G.5.
 */

import '../styles/disclosure-caret.css';

export interface DisclosureCaretOptions {
  /**
   * Доступное имя триггера (`aria-label`) — потребитель описывает, что раскрывается
   * (напр. «Все достижения» / «Язык интерфейса»). Видимая метка — в `labelHost`.
   */
  readonly ariaLabel: string;
  /**
   * Доп. класс(ы) на корень контрола (surface-специфичная привязка ширины/вида).
   * Напр. Профиль вешает `profile-achievements`, чтобы тест нашёл триггер/панель.
   */
  readonly className?: string;
  /**
   * Доп. класс(ы) на КНОПКУ-ТРИГГЕР ствола (surface CSS-/тест-хук на сам триггер).
   * Напр. nav-lang вешает `nav-lang-trigger`, чтобы тесты кликали по нему, а CSS
   * стилизовал именно триггер языка. Поведенческий класс `.disclosure-caret-trigger`
   * сохраняется — это ОДИН элемент с двумя хуками (механика ствола + вид ветки),
   * НЕ копия. Показ/скрытие панели по-прежнему держит ствол (`--open`).
   */
  readonly triggerClassName?: string;
  /**
   * Доп. класс(ы) на ПАНЕЛЬ ствола (surface CSS-/тест-хук на раскрываемый контейнер).
   * Показ/скрытие держит ствол (`.disclosure-caret--open .disclosure-caret-panel`);
   * ветка вешает свой класс лишь для вида, НЕ для собственного display-тоггла.
   */
  readonly panelClassName?: string;
  /**
   * Колбэк при смене состояния (open/closed) — потребитель может ленить рендер
   * содержимого панели до первого раскрытия. Опц.
   */
  readonly onToggle?: (open: boolean) => void;
  /**
   * Граница outside-close: элемент, внутри которого клик считается «своим»
   * (НЕ закрывает). По умолчанию — собственный root ствола (поведение nav-lang
   * НЕ меняется: его root в документе и содержит триггер+панель).
   *
   * ЗАЧЕМ (рантайм-блокер, frontend-code-reviewer 2026-06-21): Профиль НЕ кладёт
   * `handle.element` целиком — он переотцовляет ТОЛЬКО `trigger` в свой контейнер
   * `.profile-achievements` (грид значков раскрывается max-height'ом по классу
   * `--open`, панель ствола не используется). От этого root ствола оставался orphan
   * (вне документа, не содержал ни переотцованный триггер, ни грид) → outside-close
   * по `root.contains()` (а) НЕ закрывал повторным кликом по каретке (pointerdown-
   * outside закрывал, click тут же ре-открывал), (б) СХЛОПЫВАЛ полосу при тапе по
   * значку (значок «снаружи» orphan-root). Профиль передаёт сюда `.profile-achievements`
   * — реальный контейнер, содержащий И каретку-триггер, И грид значков → outside-close
   * меряется от него, оба бага закрыты, дизайн «грид растёт на месте» сохранён.
   *
   * NB: получатель — функция (`() => HTMLElement`), а не сам элемент: на момент
   * вызова `mountDisclosureCaret` триггер ещё может быть не переотцован в контейнер
   * — границу лениво измеряем в момент клика, когда DOM-дерево уже стабильно.
   */
  readonly outsideBoundary?: () => HTMLElement;
}

export interface DisclosureCaretHandle {
  /** Корневой элемент контрола — потребитель вставляет его в свой layout. */
  readonly element: HTMLElement;
  /** Хост видимой метки триггера (слева от кареты) — потребитель кладёт сюда контент. */
  readonly labelHost: HTMLElement;
  /** Хост раскрываемой панели — потребитель кладёт сюда полный контент. */
  readonly panelHost: HTMLElement;
  /** Триггер-кнопка (для тестов/фокуса). */
  readonly trigger: HTMLButtonElement;
  /** Открыть панель. */
  open(): void;
  /** Закрыть панель. */
  close(): void;
  /** Снять document-слушатели. */
  detach(): void;
}

/** Каретка `▾` — SVG-шеврон (флип на `▴` через CSS rotate класса `--open`). */
const CARET_SVG = `
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2"
       stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="M6 9l6 6l6 -6"/></svg>
`;

/**
 * Смонтировать каретный disclosure. Возвращает handle с `labelHost` (метка триггера)
 * и `panelHost` (раскрываемая панель). Потребитель наполняет оба и вставляет
 * `handle.element` в свою раскладку.
 */
export function mountDisclosureCaret(opts: DisclosureCaretOptions): DisclosureCaretHandle {
  const root = document.createElement('div');
  root.className = 'disclosure-caret';
  if (opts.className) root.classList.add(...opts.className.split(/\s+/).filter(Boolean));

  // --- Триггер: [метка-хост] [каретка]. width:100% → панель той же ширины (CSS). ---
  const trigger = document.createElement('button');
  trigger.type = 'button';
  trigger.className = 'disclosure-caret-trigger';
  // surface CSS-/тест-хук на сам триггер (один элемент = механика ствола + вид ветки).
  if (opts.triggerClassName) trigger.classList.add(...opts.triggerClassName.split(/\s+/).filter(Boolean));
  trigger.setAttribute('aria-haspopup', 'true');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-label', opts.ariaLabel);

  const labelHost = document.createElement('span');
  labelHost.className = 'disclosure-caret-label';

  const caret = document.createElement('span');
  caret.className = 'disclosure-caret-icon';
  caret.setAttribute('aria-hidden', 'true');
  caret.innerHTML = CARET_SVG;

  trigger.append(labelHost, caret);

  // --- Панель: раскрываемый контейнер той же ширины родителя (CSS width:100%). ---
  const panelHost = document.createElement('div');
  panelHost.className = 'disclosure-caret-panel';
  // surface CSS-/тест-хук на панель (вид ветки; показ/скрытие держит ствол через --open).
  if (opts.panelClassName) panelHost.classList.add(...opts.panelClassName.split(/\s+/).filter(Boolean));
  panelHost.setAttribute('role', 'region');

  root.append(trigger, panelHost);

  let open = false;

  function setOpen(next: boolean): void {
    if (next === open) return;
    open = next;
    root.classList.toggle('disclosure-caret--open', open);
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (open) {
      document.addEventListener('keydown', onKeydown, true);
      document.addEventListener('pointerdown', onOutside, true);
    } else {
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('pointerdown', onOutside, true);
    }
    opts.onToggle?.(open);
  }

  function onOutside(e: Event): void {
    // Граница «своего» клика: переданный потребителем контейнер (Профиль — реальный
    // `.profile-achievements` с триггером+гридом), иначе собственный root ствола
    // (nav-lang — его root в документе и содержит триггер+панель). Ленивый вызов:
    // на момент клика DOM-дерево стабильно (см. JSDoc outsideBoundary).
    const boundary = opts.outsideBoundary ? opts.outsideBoundary() : root;
    if (!boundary.contains(e.target as Node)) setOpen(false);
  }

  function onKeydown(e: KeyboardEvent): void {
    if (!open) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setOpen(false);
      trigger.focus();
    }
  }

  trigger.addEventListener('click', () => setOpen(!open));

  return {
    element: root,
    labelHost,
    panelHost,
    trigger,
    open: () => setOpen(true),
    close: () => setOpen(false),
    detach(): void {
      document.removeEventListener('keydown', onKeydown, true);
      document.removeEventListener('pointerdown', onOutside, true);
    },
  };
}
